import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, MessageCircle, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Link } from "react-router-dom";
import OrderFormModal from "./OrderFormModal";
import { useLang } from "../context/LanguageContext";

export default function CartDrawer() {
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, cartTotal } = useCart();
  const [showOrderForm, setShowOrderForm] = useState(false);
  const { t } = useLang();

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setShowOrderForm(true);
  };

  const handleOrderConfirm = ({ name, phone, address }, orderId) => {
    const lines = cart.map(item => `  - ${item.mushroom.name} (${item.mushroom.uom || "250g"}) Rs.${item.mushroom.price} x${item.quantity} = Rs.${item.mushroom.price * item.quantity}`);
    const orderRef = orderId ? `\nOrder ID: #${orderId.slice(0, 8).toUpperCase()}` : "";
    const msg = `Hello PIIKK Agro Farm Fresh Mushrooms! I have placed an order:${orderRef}\n\n${lines.join('\n')}\n\nTotal: Rs.${cartTotal}\n\nCustomer Details:\nName: ${name}\nPhone: ${phone}\nAddress: ${address}\n\nKindly acknowledge this order and let me know the expected delivery date and time. Thank you!`;
    return `https://wa.me/916362516143?text=${encodeURIComponent(msg)}`;
  };

  return (
    <>
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            data-testid="cart-backdrop"
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 220 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[420px] z-[70] flex flex-col shadow-2xl border-l"
            style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--c-border)' }}>
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" style={{ color: '#D97A46' }} />
                <h2 className="font-serif text-xl font-semibold" style={{ color: 'var(--c-text)' }}>{t('cart.title')}</h2>
                {cart.length > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'rgba(217,122,70,0.1)', color: '#D97A46' }}>
                    {cart.length} {t('cart.items')}
                  </span>
                )}
              </div>
              <button data-testid="cart-close-button" onClick={() => setIsCartOpen(false)}
                className="p-2 rounded-full transition-all" style={{ color: 'var(--c-muted)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 py-16">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--c-surface-alt)' }}>
                    <ShoppingBag className="w-8 h-8" style={{ color: 'var(--c-border)' }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('cart.empty')}</p>
                  <Link to="/shop" onClick={() => setIsCartOpen(false)} data-testid="cart-browse-link"
                    className="px-5 py-2.5 text-white rounded-full text-sm font-medium transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: '#D97A46' }}>
                    {t('cart.browseVarieties')}
                  </Link>
                </div>
              ) : (
                cart.map(({ mushroom, quantity }) => (
                  <motion.div key={mushroom.id} layout initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                    className="flex gap-3 p-3 rounded-xl border" style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-bg)' }}
                    data-testid={`cart-item-${mushroom.id}`}>
                    <img src={mushroom.image} alt={mushroom.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm leading-tight truncate" style={{ color: 'var(--c-text)' }}>{mushroom.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--c-muted)' }}>{mushroom.uom || "250g"} · Organic</p>
                      <p className="font-semibold text-sm mt-1" style={{ color: '#D97A46' }}>
                        Rs.{mushroom.price * quantity}
                        <span className="font-normal text-xs ml-1" style={{ color: 'var(--c-muted)' }}>(Rs.{mushroom.price} each)</span>
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button data-testid={`cart-remove-${mushroom.id}`} onClick={() => removeFromCart(mushroom.id)}
                        className="p-1 transition-colors hover:text-red-500" style={{ color: 'var(--c-muted)' }}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-1.5 rounded-full px-2 py-1 border" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}>
                        <button data-testid={`cart-decrement-${mushroom.id}`}
                          onClick={() => quantity === 1 ? removeFromCart(mushroom.id) : updateQuantity(mushroom.id, quantity - 1)}
                          className="w-5 h-5 flex items-center justify-center transition-colors" style={{ color: 'var(--c-muted)' }}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-medium w-5 text-center" style={{ color: 'var(--c-text)' }} data-testid={`cart-qty-${mushroom.id}`}>{quantity}</span>
                        <button data-testid={`cart-increment-${mushroom.id}`}
                          onClick={() => updateQuantity(mushroom.id, quantity + 1)}
                          className="w-5 h-5 flex items-center justify-center transition-colors" style={{ color: 'var(--c-muted)' }}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="p-5 border-t space-y-4" style={{ borderColor: 'var(--c-border)' }}>
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('cart.total')}</span>
                  <span className="font-serif text-2xl font-semibold" style={{ color: 'var(--c-text)' }} data-testid="cart-total">
                    Rs.{cartTotal}
                  </span>
                </div>
                <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('cart.deliveryNote')}</p>
                <button data-testid="whatsapp-checkout-button" onClick={handleCheckout}
                  className="w-full py-4 rounded-full font-semibold text-white flex items-center justify-center gap-2.5 transition-all hover:-translate-y-0.5 hover:shadow-md"
                  style={{ backgroundColor: '#25D366' }}>
                  <MessageCircle className="w-5 h-5" />
                  {t('cart.checkout')}
                </button>
                <p className="text-center text-xs text-[var(--c-muted)]">
                  {t('cart.checkoutNote')}
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>

      {showOrderForm && (
        <OrderFormModal
          itemLines={cart.map(item => `${item.mushroom.name} (${item.mushroom.uom || "250g"}) Rs.${item.mushroom.price} x${item.quantity} = Rs.${item.mushroom.price * item.quantity}`)}
          total={`Rs.${cartTotal}`}
          cartItems={cart}
          cartTotal={cartTotal}
          onClose={() => setShowOrderForm(false)}
          onConfirm={handleOrderConfirm}
        />
      )}
    </> 
  );
}
